import {
  cancelForAnyReason,
  checkInBaggage,
  climateCompensation,
  flexibleTicket,
} from '@eti/products';
import {
  type BrandedFare as BrandedFareType,
  type CheckedBaggage,
  type CabinBaggageDetails,
  type CabinBaggageTripSummary,
  type TravelerPrice,
  type SellSpecificationTravelerNew,
  type SellSpecificationBaggageNew,
  type PaymentMethodPrice,
  TripType,
} from '@eti/schema-types';
import { constants, price as priceUtil } from '@eti/utils';
import sumBy from 'lodash/sumBy';
import { ArrowLeft, ArrowRight, ArrowsLeftRight as ReturnTripArrow } from '@phosphor-icons/react';
import { DIRECTIONS } from '@eti/providers';
import subtract from 'lodash/subtract';
import type { BoundType, SegmentsType } from '../../../order-details/models';
import { getTravelerPrices } from '../../../../utils/travelers';
import { getTripDisplayPrice } from '../../../result/utils/resultUtils';
import {
  NO_CABIN_BAGGAGE,
  NO_CABIN_BAGGAGE_AVAILABLE,
  INCLUDED_CABIN_BAGGAGE,
  INCLUDED_CHECKED_BAGGAGE,
} from '../../../../constants/includedBaggageConstants';
import type {
  AvailableExtraProductsType,
  PreSelectedExtraProducts,
  PreSelectedProductItem,
} from './branded-fares/model';
import { INCLUDED, SELLABLE } from './branded-fares/constants';

export const getProductDescription = (
  productId: number,
  t: (key: any, ...params: any[]) => string,
) => {
  switch (productId) {
    case flexibleTicket.id:
      return t('Result.Page.FlexibleTicket.Description.Text');
    case cancelForAnyReason.id:
      return t('Result.Page.CancelForAnyReasonTicket.Description.Text');
    case climateCompensation.id:
      return t('co2CompensationAbstract');
    case checkInBaggage.id:
      return t('Product.CheckedBaggagePerTraveler.Description.Label');
    default:
      return null;
  }
};

export const getProductNamesFromIDs = (
  productsInFareNotManuallyRemoved: number[],
  productIdNameMap: Record<number, string>,
  t: (key: any, ...params: any[]) => string,
): string => {
  const productsLength = productsInFareNotManuallyRemoved.length;
  if (productsLength === 1) {
    const productId = productsInFareNotManuallyRemoved[0];
    return productIdNameMap[productId];
  }

  if (productsLength === 2) {
    const productsNames = productsInFareNotManuallyRemoved
      .map((productId: number) => productIdNameMap[productId])
      .join(` ${t('YourSelectedTrip.Preselected.Product.And')} `);
    return productsNames;
  }

  if (productsLength === 3) {
    const productsNames = productsInFareNotManuallyRemoved.map(
      (productId: number) => productIdNameMap[productId],
    );
    return `${productsNames[0]}, ${productsNames[1]} ${t(
      'YourSelectedTrip.Preselected.Product.And',
    )} ${productsNames[2]}`;
  }

  return '';
};

const resolvePrice = (brandedFareData: BrandedFareType, tripPriceDisplayType: string) => {
  const { travelers, travelerPrices } = brandedFareData;
  const groupedTravelerPrices = getTravelerPrices(travelers!, travelerPrices!);

  return getTripDisplayPrice(tripPriceDisplayType)(groupedTravelerPrices);
};

export const getFormattedPrice = (brandedFareData: BrandedFareType, tripPriceDisplayType: string) =>
  priceUtil.formatPrice(resolvePrice(brandedFareData, tripPriceDisplayType));

export const formatBrandedFareName = (fareName: string) => {
  const fareNameList = fareName.toLowerCase().split(' ');
  const brandedFareName = fareNameList.map((name) => name.charAt(0).toUpperCase() + name.slice(1));

  return brandedFareName.join(' ');
};

export const getFormattedIncrementalPrice = (
  brandedFarePrice: number,
  selectedTripPrice: number,
  isRTL: boolean,
) => {
  // TODO: Send fare price and accessible fare price in an object from here
  const differenceInPrice = subtract(brandedFarePrice, selectedTripPrice);
  const absoluteDifferenceInPrice = Math.abs(differenceInPrice);

  const formattedAbsoluteFarePrice = priceUtil.formatPrice(absoluteDifferenceInPrice);

  const incrementalPriceSign = differenceInPrice < 0 ? '-' : '+';

  const formattedAndSignedFarePrice = isRTL
    ? `${formattedAbsoluteFarePrice} ${incrementalPriceSign}`
    : `${incrementalPriceSign} ${formattedAbsoluteFarePrice}`;

  return formattedAndSignedFarePrice;
};

let latestId = 0;
const getNextId = () => {
  latestId += 1;
  return latestId;
};

const addMessage = (description: string) => ({
  description,
  id: getNextId(),
});

const areListsIdentical = (list1: number[], list2: number[]) =>
  JSON.stringify(list1) === JSON.stringify(list2);

interface ToasMessageText {
  multiProductRemoved: string;
  multiProductReAdded: string;
  oneOfAdded: string;
  oneOfRemoved: string;
  singleProductRemoved: string;
  singleProductReAdded: string;
}

export const getToastMessage = (
  oldListOfRemovedProductsId: number[], // Old state
  newListOfRemovedProductsId: number[], // After user intervention but Before state update
  totalProducts: number,
  toastMessageText: ToasMessageText,
) => {
  if (areListsIdentical(oldListOfRemovedProductsId, newListOfRemovedProductsId)) {
    return [];
  }

  const newlyUpdatedProductsList =
    newListOfRemovedProductsId.length > 1
      ? newListOfRemovedProductsId.filter(
          (productID) => !oldListOfRemovedProductsId.includes(productID),
        )
      : newListOfRemovedProductsId;

  const oldListWithoutNewListProducts = oldListOfRemovedProductsId
    .filter((productId) => !newlyUpdatedProductsList.includes(productId))
    .filter(Boolean);

  const oldListOfRemovedProductsIdLength = oldListOfRemovedProductsId.length;
  const oldListWithoutNewListProductsLength = oldListWithoutNewListProducts.length;
  const newListOfRemovedProductsIdLength = newListOfRemovedProductsId.length;
  const newlyUpdatedProductsListLength = newlyUpdatedProductsList.length;

  const isProductRemoved = newlyUpdatedProductsListLength - oldListOfRemovedProductsIdLength === 1;
  const isSingleProductRemoved = isProductRemoved && totalProducts === 1;
  const areProductRemoved = newlyUpdatedProductsListLength - oldListOfRemovedProductsIdLength > 1;
  const isProductAdded = oldListOfRemovedProductsIdLength - newlyUpdatedProductsListLength === 1;
  const isSingleProductAdded = isProductAdded && totalProducts === 1;
  const areProductAdded = oldListOfRemovedProductsIdLength - newlyUpdatedProductsListLength > 1;
  const isOneProductAddedAndOneRemoved =
    oldListOfRemovedProductsIdLength - newListOfRemovedProductsIdLength === 0 &&
    newlyUpdatedProductsListLength === 1;
  const isOneMoreProductRemoved =
    newListOfRemovedProductsIdLength - oldListOfRemovedProductsIdLength === 1 &&
    newlyUpdatedProductsListLength === 1;
  const isOneMoreProductAdded =
    oldListWithoutNewListProductsLength - newListOfRemovedProductsIdLength === 1 &&
    newlyUpdatedProductsListLength === 1;
  const isMultipleProductRemovedAndOneReadded =
    newlyUpdatedProductsListLength > 1 && oldListOfRemovedProductsIdLength === 1;
  const isMultipleProductAddedAndOneRemoved =
    oldListWithoutNewListProductsLength > 1 && newlyUpdatedProductsListLength === 1;

  const messages = [];
  if (isOneProductAddedAndOneRemoved) {
    messages.push(addMessage(toastMessageText.oneOfAdded));
    messages.push(addMessage(toastMessageText.oneOfRemoved));
  } else if (isSingleProductRemoved) {
    messages.push(addMessage(toastMessageText.singleProductRemoved));
  } else if (isSingleProductAdded) {
    messages.push(addMessage(toastMessageText.singleProductReAdded));
  } else if (isMultipleProductRemovedAndOneReadded) {
    messages.push(addMessage(toastMessageText.oneOfAdded));
    messages.push(addMessage(toastMessageText.multiProductRemoved));
  } else if (isMultipleProductAddedAndOneRemoved) {
    messages.push(addMessage(toastMessageText.oneOfRemoved));
    messages.push(addMessage(toastMessageText.multiProductReAdded));
  } else if (areProductRemoved) {
    messages.push(addMessage(toastMessageText.multiProductRemoved));
  } else if (areProductAdded) {
    messages.push(addMessage(toastMessageText.multiProductReAdded));
  } else if (isProductRemoved || isOneMoreProductRemoved) {
    messages.push(addMessage(toastMessageText.oneOfRemoved));
  } else if (isProductAdded || isOneMoreProductAdded) {
    messages.push(addMessage(toastMessageText.oneOfAdded));
  }

  return messages;
};

export const getIncludedBaggageText = (
  { pieces, weight, weightUnit }: CheckedBaggage | CabinBaggageDetails,
  textKey: string,
  t: (key: any, ...params: any[]) => string,
) => {
  const { LRE, PDF } = constants.unicodeControlCharacters;
  if (pieces && weight && weightUnit) {
    return t(textKey, `${LRE}${pieces}×${weight}${weightUnit}${PDF}`);
  }

  if (pieces) {
    return t(textKey, pieces);
  }

  if (pieces === null && weight && weightUnit) {
    return t(textKey, `${weight}${weightUnit}${PDF}`);
  }

  return '';
};

export const getPersonalAndCabinBaggageInfo = (
  isPersonalItemIncluded: boolean,
  t: (key: any, ...params: any[]) => string,
  cabinBaggageTripSummary?: CabinBaggageTripSummary,
) => {
  let personalAndCabinText = '';
  let isIncluded = true;
  const cabinBaggageStatus =
    cabinBaggageTripSummary?.cabinBaggageTravelerDetails?.[0]?.cabinBaggageStatus;
  const cabinBaggageDetails =
    cabinBaggageTripSummary?.cabinBaggageTravelerDetails?.[0]?.cabinBaggageDetails;
  if (
    isPersonalItemIncluded &&
    cabinBaggageStatus &&
    cabinBaggageDetails &&
    cabinBaggageStatus === INCLUDED_CABIN_BAGGAGE
  ) {
    personalAndCabinText = getIncludedBaggageText(
      cabinBaggageDetails,
      'BrandedFares.PersonalItemAndCabinBaggageIncluded.Text',
      t,
    );
  } else if (
    isPersonalItemIncluded &&
    (cabinBaggageStatus === NO_CABIN_BAGGAGE || cabinBaggageStatus === NO_CABIN_BAGGAGE_AVAILABLE)
  ) {
    personalAndCabinText = t('BrandedFares.PersonalItemIncludedButNoCabinBaggage.Text');
  } else if (
    !isPersonalItemIncluded &&
    cabinBaggageDetails &&
    cabinBaggageStatus === INCLUDED_CABIN_BAGGAGE
  ) {
    personalAndCabinText = getIncludedBaggageText(
      cabinBaggageDetails,
      'BrandedFares.NoPersonalItemButCabinBaggageIncluded.Text',
      t,
    );
  } else if (
    !isPersonalItemIncluded &&
    (cabinBaggageStatus === NO_CABIN_BAGGAGE || cabinBaggageStatus === NO_CABIN_BAGGAGE_AVAILABLE)
  ) {
    personalAndCabinText = t('BrandedFares.NoPersonalItemAndNoCabinBaggageIncluded.Text');
    isIncluded = false;
  }

  return { isIncluded, personalAndCabinText };
};

export const getProductIdByATPCOCode = (code: string) => {
  switch (code) {
    case '068':
    case '059':
    case '06I':
      return flexibleTicket.id;
    case '056':
      return cancelForAnyReason.id;
    case '0EO':
      return climateCompensation.id;
    default:
      return null;
  }
};

const isSellableFeature = (
  marketingCarrier: { code: string },
  carriersWithSellableFeatures: string[],
  isFeatureSellable: boolean,
) => {
  const isMarketingCarrierInCarrierList = carriersWithSellableFeatures?.includes(
    marketingCarrier.code,
  );

  return isFeatureSellable && isMarketingCarrierInCarrierList;
};

export const getProductsIdFromSelectedFare = (
  selectionKey: string,
  brandedFares: BrandedFareType[],
  marketingCarrier: { code: string },
  carriersWithSellableFeatures: string[],
) => {
  const [selectedFare] = brandedFares.filter((fare) => fare.selectionKey === selectionKey);
  const tripSummaries =
    selectedFare?.checkedBaggageSummary?.tripSummaries &&
    selectedFare?.checkedBaggageSummary?.tripSummaries[0];
  const isCheckedBaggageIncluded = tripSummaries?.baggageStatus === INCLUDED_CHECKED_BAGGAGE;
  const productsId =
    selectedFare?.features &&
    (selectedFare?.features
      .map((feature) => {
        if (feature?.code) {
          const productId = getProductIdByATPCOCode(feature?.code);
          const isFeatureSellable = isSellableFeature(
            marketingCarrier,
            carriersWithSellableFeatures,
            feature?.availability === SELLABLE,
          );
          const isFeatureIncluded = feature?.availability === INCLUDED;
          if (
            productId &&
            (isFeatureIncluded || isFeatureSellable) &&
            [flexibleTicket.id, cancelForAnyReason.id, climateCompensation.id].includes(productId)
          ) {
            return productId;
          }
        }
        return false;
      })
      .filter(Boolean) as number[]);

  if (isCheckedBaggageIncluded) {
    productsId?.push(checkInBaggage.id);
  }

  return productsId;
};

export const getDefaultAndRecommendedFare = (
  travelerPrices: TravelerPrice[],
  brandedFares: BrandedFareType[],
) => {
  const defaultFarePrice = travelerPrices[0]?.price.price.value;
  const defaultSelectedBrandedFareIndex = brandedFares.findIndex(
    (brandedFare) =>
      brandedFare.travelerPrices &&
      brandedFare.travelerPrices[0]?.price.price.value === defaultFarePrice,
  );

  if (!localStorage.getItem('recommendedFareKey')) {
    localStorage.setItem(
      'recommendedFareKey',
      brandedFares[defaultSelectedBrandedFareIndex + 1]?.selectionKey,
    );
  }

  return {
    defaultSelectedFareKey: brandedFares[defaultSelectedBrandedFareIndex]?.selectionKey,
    recommendedFareKey: localStorage.getItem('recommendedFareKey'),
  };
};

export const getCommonProductsInFareAndTrip = (
  productsInFare: number[] = [],
  preSelectedExtraProducts: PreSelectedExtraProducts[],
) => {
  const preSelectedProductIds = preSelectedExtraProducts?.map(({ productId }) => productId);
  const commonProducts = preSelectedProductIds?.filter((productId) =>
    productsInFare.includes(productId),
  );

  return commonProducts;
};

export const shouldShowInformationBox = (
  carriersToHideInformationBox: string[],
  ticketingCarrierCodes: string[],
) => {
  const hideInformationBoxForCarrier = ticketingCarrierCodes?.some(
    (ticketingCarrierCode) => carriersToHideInformationBox?.includes(ticketingCarrierCode),
  );

  return !hideInformationBoxForCarrier;
};

export const modifyTripBrandedFareName = (
  bounds: BoundType[],
  brandedFares: BrandedFareType[],
  defaultSelectedFareKey: string,
) => {
  const { name } =
    brandedFares?.find(({ selectionKey }) => selectionKey === defaultSelectedFareKey) || {};

  return bounds.map((bound: { segments: SegmentsType }) => {
    return {
      ...bound,
      segments: bound.segments.map((segment) => {
        switch (segment?.__typename) {
          case 'TripSegment':
            return { ...segment, brandedFareInformation: { name, __typename: 'BrandedFareInfo' } };
          case 'EventSegment':
            return segment;
          default:
            return null;
        }
      }),
    };
  });
};

export const findFareProductById = (products: PreSelectedExtraProducts[], productId: number) => {
  return products?.find((product) => product.productId === productId);
};

export const getFormattedFarePrice = (
  brandedFareData: BrandedFareType,
  tripPriceDisplayType: string,
  selectedTripPrice: number,
  shouldDisplayIncrementalPricing: boolean,
  isRTL: boolean,
) => {
  const { travelers, travelerPrices } = brandedFareData;
  const selectedFarePrices = getTravelerPrices(travelers!, travelerPrices!);
  const selectedFarePrice = getTripDisplayPrice(tripPriceDisplayType)(selectedFarePrices);
  const farePrice = priceUtil.formatPrice(selectedFarePrice);

  const formattedFarePrice = shouldDisplayIncrementalPricing
    ? getFormattedIncrementalPrice(selectedFarePrice, selectedTripPrice, isRTL)
    : farePrice;

  return formattedFarePrice;
};

export const getTotalPrice = (
  selectedBrandedFare: BrandedFareType,
  priceDisplayType: string,
  finalProductIdsToPurchase?: number[],
  priceUpdatedProducts?: PreSelectedProductItem[],
) => {
  let totalPrice;
  const farePrice = resolvePrice(selectedBrandedFare, priceDisplayType);

  totalPrice = farePrice;
  if (finalProductIdsToPurchase && finalProductIdsToPurchase.length > 0) {
    const totalPriceOfPreSelectedProducts = priceUpdatedProducts?.reduce(
      (productsPrice, { productId, price }: { productId: number; price: number }) => {
        if (finalProductIdsToPurchase.includes(productId)) {
          return productsPrice + price;
        }

        return productsPrice;
      },
      0,
    );

    totalPrice += totalPriceOfPreSelectedProducts;
  }

  return priceUtil.formatPrice(totalPrice);
};

interface ExtraProductsText {
  cfarTicketTotal: string;
  checkedBaggageTotal: string;
  climateCompensationTotal: string;
  flexibleTicketTotal: string;
}

export const getProductsTextAndTotalPrice = (
  availableExtraProducts: AvailableExtraProductsType[],
  extraProductsText: ExtraProductsText,
  finalProductIdsToPurchase: number[],
) => {
  const productsTextAndTotalPrice = finalProductIdsToPurchase
    .map((id: number) => {
      if (id === flexibleTicket.id) {
        const text = extraProductsText.flexibleTicketTotal;
        const flexibleTicketConfig = availableExtraProducts.find(
          ({ productId }: { productId: string }) => productId === String(flexibleTicket.id),
        );

        if (!flexibleTicketConfig) {
          return false;
        }

        const flexibleTicketSellSpecification =
          flexibleTicketConfig?.sellSpecification as SellSpecificationTravelerNew;
        const flexibleTicketPrices = flexibleTicketSellSpecification?.sellPriceTravelers;
        const flexibleTicketTotalPrices = sumBy(flexibleTicketPrices, 'price.price.value');

        return {
          text,
          totalPrice: flexibleTicketTotalPrices,
          id,
        };
      }

      if (id === cancelForAnyReason.id) {
        const text = extraProductsText.cfarTicketTotal;
        const cfarTicketConfig = availableExtraProducts.find(
          ({ productId }: { productId: string }) => productId === String(cancelForAnyReason.id),
        );

        if (!cfarTicketConfig) {
          return false;
        }

        const cfarTicketSellSpecification = cfarTicketConfig?.sellSpecification;
        const cfarTicketTotalPrices = sumBy([cfarTicketSellSpecification], 'price.price.value');

        return {
          text,
          totalPrice: cfarTicketTotalPrices,
          id,
        };
      }

      if (id === checkInBaggage.id) {
        const text = extraProductsText.checkedBaggageTotal;
        const checkInBaggageConfig = availableExtraProducts.find(
          ({ productId }: { productId: string }) => productId === String(checkInBaggage.id),
        );

        if (!checkInBaggageConfig) {
          return false;
        }

        const checkInBaggageSellSpecification =
          checkInBaggageConfig?.sellSpecification as SellSpecificationBaggageNew;

        const checkInBaggageSellPriceBaggage =
          checkInBaggageSellSpecification?.sellPriceBaggage?.flatMap((baggage) => baggage?.options);
        const checkInBaggageTotalPrice = sumBy(checkInBaggageSellPriceBaggage, 'price.price.value');

        return {
          text,
          totalPrice: checkInBaggageTotalPrice,
          id,
        };
      }

      if (id === climateCompensation.id) {
        const text = extraProductsText.climateCompensationTotal;
        const climateCompensationConfig = availableExtraProducts.find(
          ({ productId }: { productId: string }) => productId === String(climateCompensation.id),
        );

        if (!climateCompensationConfig) {
          return false;
        }

        const climateCompensationSellSpecification = climateCompensationConfig?.sellSpecification;

        const climateCompensationTotalPrice =
          climateCompensationSellSpecification.price.price.value;

        return {
          text,
          totalPrice: climateCompensationTotalPrice,
          id,
        };
      }

      return false;
    })
    .filter(Boolean);

  return productsTextAndTotalPrice;
};

export const getPaymentMethodPrices = (paymentMethodPrices: PaymentMethodPrice[]) => {
  return paymentMethodPrices.map((method) => {
    if (typeof method.price === 'number') {
      return {
        ...method,
        label: method.name,
        price: method?.price ?? 0,
        value: method?.price ?? 0,
      };
    }
    return {
      ...method,
      label: method.name,
      price: method?.price?.value ?? 0,
      value: method?.price?.value ?? 0,
    };
  });
};

export const getTripTypeIcon = (direction: 'ltr' | 'rtl', tripType?: TripType) => {
  if (tripType === TripType.Return) {
    return ReturnTripArrow;
  }

  if (tripType === TripType.OneWay) {
    return direction === DIRECTIONS.LTR ? ArrowRight : ArrowLeft;
  }

  return ArrowRight;
};
